import { ElectricBikeRequirement } from "./electric-bike-requirement"
import { ServiceBikeGearSystem, ServiceBikeType } from "./service-bike"
import { ServicePackage, ServicePackagePriceEntry } from "./service-package"

export interface BikeCharacteristics {
  type: ServiceBikeType | null
  /**
   * Optional. CRM-internal: when set, also matches `onlyApplicableToCustomBikeTypes`.
   * Marketplace callers leave this `undefined` — custom types never reach the
   * marketplace, so no custom applicability rule will ever match there.
   */
  customBikeTypeId?: string | null
  isElectric: boolean | null
  gearSystem: ServiceBikeGearSystem | null
  isClubMember: boolean
}

export interface PriceResolutionResult {
  price: number
  includedMaterialCost: number | null
  matchedEntryId: string | null
}

export class ServicePackagePriceResolver {
  /**
   * Resolves the price for a package based on bike characteristics.
   * Returns the cheapest matching price entry, or null if no entries match.
   * When priceListId is provided, only entries that belong to that price list
   * (or entries with no price list restriction) are considered.
   */
  static resolvePrice(
    pkg: ServicePackage,
    bike: BikeCharacteristics,
    priceListId?: string,
  ): PriceResolutionResult | null {
    const matchingEntries = ServicePackagePriceResolver.getMatchingEntries(
      pkg.priceEntries,
      bike,
      priceListId,
    )

    if (matchingEntries.length === 0) {
      return null
    }

    // Find the cheapest matching entry
    const cheapestEntry = matchingEntries.reduce((cheapest, entry) =>
      entry.price < cheapest.price ? entry : cheapest,
    )

    return {
      price: cheapestEntry.price,
      includedMaterialCost: cheapestEntry.includedMaterialCost ?? null,
      matchedEntryId: cheapestEntry.id ?? null,
    }
  }

  /**
   * Returns all price entries that match the given bike characteristics.
   * When priceListId is provided, filters to entries belonging to that price list
   * (or entries with no price list restriction).
   */
  static getMatchingEntries(
    entries: ServicePackagePriceEntry[],
    bike: BikeCharacteristics,
    priceListId?: string,
  ): ServicePackagePriceEntry[] {
    return entries.filter((entry) => {
      if (!ServicePackagePriceResolver.entryMatchesBike(entry, bike)) {
        return false
      }
      if (
        priceListId &&
        entry.priceListIds.length > 0 &&
        !entry.priceListIds.includes(priceListId)
      ) {
        return false
      }
      return true
    })
  }

  /**
   * Checks if a single price entry matches the given bike characteristics.
   */
  static entryMatchesBike(
    entry: ServicePackagePriceEntry,
    bike: BikeCharacteristics,
  ): boolean {
    // Check bike type (empty array on BOTH lists = matches all).
    // A bike matches if either:
    //  - its enum type is in onlyApplicableToBikeTypes, OR
    //  - its customBikeTypeId is in onlyApplicableToCustomBikeTypes.
    // When bike.type is null and both lists are non-empty, no match.
    const hasEnumRestriction = entry.onlyApplicableToBikeTypes.length > 0
    const hasCustomRestriction =
      entry.onlyApplicableToCustomBikeTypes.length > 0
    if (hasEnumRestriction || hasCustomRestriction) {
      const matchesEnum =
        bike.type !== null &&
        entry.onlyApplicableToBikeTypes.includes(bike.type)
      const matchesCustom =
        bike.customBikeTypeId != null &&
        entry.onlyApplicableToCustomBikeTypes.includes(bike.customBikeTypeId)
      if (!matchesEnum && !matchesCustom) {
        return false
      }
    }

    // Check electric requirement
    if (bike.isElectric !== null) {
      if (
        entry.electricRequirement ===
          ElectricBikeRequirement.REQUIRES_ELECTRIC &&
        !bike.isElectric
      ) {
        return false
      }
      if (
        entry.electricRequirement ===
          ElectricBikeRequirement.REQUIRES_NON_ELECTRIC &&
        bike.isElectric
      ) {
        return false
      }
    }

    // Check gear system (empty array = matches all)
    // When bike.gearSystem is null, skip the check (gear system is not always known)
    if (entry.onlyApplicableToGearSystems.length > 0) {
      if (
        bike.gearSystem !== null &&
        !entry.onlyApplicableToGearSystems.includes(bike.gearSystem)
      ) {
        return false
      }
    }

    // Check club membership requirement
    if (entry.requiresClubMembership && !bike.isClubMember) {
      return false
    }

    return true
  }

  /**
   * Checks if a package is available for a given bike.
   * A package is available if at least one price entry matches the bike.
   */
  static isPackageAvailableForBike(
    pkg: ServicePackage,
    bike: BikeCharacteristics,
    priceListId?: string,
  ): boolean {
    return (
      ServicePackagePriceResolver.getMatchingEntries(
        pkg.priceEntries,
        bike,
        priceListId,
      ).length > 0
    )
  }

  /**
   * Returns the minimum and maximum prices from all price entries.
   * Useful for displaying price ranges in the UI.
   */
  static getPriceRange(entries: ServicePackagePriceEntry[]): {
    min: number
    max: number
  } | null {
    if (entries.length === 0) {
      return null
    }

    const prices = entries.map((e) => e.price)
    return {
      min: Math.min(...prices),
      max: Math.max(...prices),
    }
  }

  /**
   * Minutes of work for a package whose price was resolved to `price`. Snapshots
   * only keep the price, so the entry is found by it. Falls back to the
   * cheapest entry that has minutes, the one resolvePrice picks first, when the
   * price was overridden by hand.
   */
  static laborTimeMinutesForPrice(
    entries: Pick<ServicePackagePriceEntry, "price" | "laborTimeMinutes">[],
    price: number,
  ): number | null {
    const withMinutes = entries
      .filter((entry) => entry.laborTimeMinutes !== null)
      .sort((a, b) => a.price - b.price)

    const samePrice = withMinutes.find((entry) => entry.price === price)
    return (samePrice ?? withMinutes[0])?.laborTimeMinutes ?? null
  }
}
